import { Stack } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppLoader from "../../../../components/AppLoader";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import GlobalButton from "../../../../components/CustomButton";
import AddEditAttributeDialog from "../../../../components/Dialog/AddEditAttributeDialog";
import GlobalTable from "../../../../components/Tables/GlobalTable";
import useDebounce from "../../../../hooks/useDebounce";
import { deleteAttribute, getAllAttributes } from "../../../../services/attributeFn";
import { handleOpenSnackBar } from "../../../../utils";
import { deleteConfirmAlert } from "../../../../components/Alert";
import { CSVLink } from "react-csv";
import moment from "moment";
import { APP_ICONS } from "../../../../assets";

const headerData = [
    "createdAt",
    "serialNumber",
    "name",
    "values",
    "actions",
];

const tableRows = [
    "created_date",
    "sortOrder",
    "attribute_name",
    "attribute_values",
    "actions",
];

export function Attribute() {
    const navigate = useNavigate();
    const openDialog = useRef(null);
    const { t } = useTranslation(["catalogue", "common"]);
    const [isLoading, setIsLoading] = useState(false);
    const [allAttributes, setAllAttributes] = useState([]);
    const userData = useSelector((state) => state.userData.user);
    const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
    const debouncedSearchTerm = useDebounce(searchKeys.attributeName, 1000);


    const fetchAllAttributes = async () => {
        try {
            const params = {
                search: debouncedSearchTerm,
            };
            setIsLoading(true);
            const response = await getAllAttributes(params);
            if (response.data.status === "success") {
                let data = response?.data.data.reverse();
                setAllAttributes(data);
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (userData) {
            fetchAllAttributes();
        }
    }, [userData, debouncedSearchTerm]);


    const prepareExportData = () => {

        return allAttributes.map((item) => ({
            "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "N/A",
            "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "N/A",
            "Name In Romanian": item.label?.ro || "N/A",
            "Name In English": item.label?.en || "N/A",
            "value": item.value || "N/A",
            "Sorting Number": item.sortOrder || "N/A",
            "Status": item.status ? "Active" : "Inactive",
        }));

    };

    const renderHeaderAction = () => {
        return (
            <Stack
                gap={1}
                flexGrow={1}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"flex-end"}
            >
                <GlobalButton
                    variant={"contained"}
                    btnText={"addNew"}
                    handleClickBtn={() => {
                        openDialog.current.openDialog({
                            type: "add",
                            data: null,
                            getData: fetchAllAttributes,
                        });
                    }}
                    styleProps={{
                        width: "auto",
                        height: 40,
                    }}
                />

                <CSVLink
                    data={prepareExportData()}
                    filename={"attributes.csv"}
                    className="csv-link"
                    target="_blank"
                >
                    <GlobalButton
                        variant={"export_button"}
                        btnText={t("export", { ns: "common" })}
                        startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
                        styleProps={{
                            width: "auto",
                            height: 40,
                        }}
                    />
                </CSVLink>

            </Stack>
        );
    };

    const handleEdit = (row) => {
        openDialog.current.openDialog({
            type: "edit",
            data: row?.row,
            getData: fetchAllAttributes,
        });
    };

    const handleNavigateToAttributeValue = (row) => {
        console.log(row, "MERELUND");
        if (!row) return handleOpenSnackBar("error", t("noDataFound", { ns: "common" }));
        navigate(`/catalogue/attribute-value/${row?._id}`);
    };

    const handleDelete = (row) => {
        deleteConfirmAlert({
            title: t("deleteConfirmation", { ns: "common" }),
            text: t("deleteConfirmationText", { ns: "common" }),
            confirmButtonText: t("delete", { ns: "common" }),
            cancelButtonText: t("cancel", { ns: "common" }),
        }).then(async (result) => {
            if (result) {
                try {
                    const response = await deleteAttribute(row?.row?._id);
                    if (response.data.status === "success") {
                        fetchAllAttributes();
                        handleOpenSnackBar("success", response.data.message);
                    } else {
                        handleOpenSnackBar("error", response.data.message);
                    }
                } catch (error) {
                    handleOpenSnackBar("error", error.message);
                }
            }
        });
    }

    const renderTable = useMemo(() => {
        return (
            <GlobalTable
                minWidth={1200}
                tableHeader={headerData}
                displayRows={tableRows}
                tableData={allAttributes || []}
                showActions={true}
                showEdit={true}
                showDelete={true}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                handleNavigateToAttributeValue={handleNavigateToAttributeValue}

            />
        );
    }, [allAttributes]);

    return (
        <React.Fragment>
            <AppLoader isVisible={isLoading} />
            <PaperContainer
                title={t("attributeList")}
                subtitle={t("attributeDescription")}
                headerActions={renderHeaderAction()}
            >
                {renderTable}
            </PaperContainer>

            <AddEditAttributeDialog ref={openDialog} />
            {/* <AddEditAttributeValuesDialog ref={openAddEditAttributeValuesDialog} /> */}

        </React.Fragment>
    );
}


